import type { OfflineDb } from '../infrastructure/dexie/offline-db.js';

/**
 * purge-old-outbox (pwa-offline-first Slice 7 MEDIUM fix — adversarial
 * review) — bounded retention for the outbox table. Nothing before this
 * fix ever deleted an outbox ROW: `cleanupSyncedBlobs` (Slice 4/6) only
 * ever swept the linked BLOB, leaving the outbox row (and, for a
 * `discarded` item, sometimes its blobs too — cleanupSyncedBlobs only runs
 * right after a successful SYNC, never after a discard) behind forever.
 * Over a long-lived install this both grows device storage unboundedly
 * AND directly feeds `/pending`'s durable-warning live query (see
 * `PendingItemsPage.tsx`), which re-scans the `synced` state index on
 * every outbox write — bounding the row count to a 7-day window keeps
 * that scan small without needing a new indexed column (Dexie schema
 * bumps are additive-only and carry their own migration-test cost; a
 * small bounded query was the pragmatic fix here — see this MEDIUM
 * finding's own scope).
 */
export const OUTBOX_RETENTION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isPurgeableState(state: unknown): boolean {
  return state === 'synced' || state === 'discarded';
}

/**
 * purgeOldOutboxRows — deletes every outbox row in a terminal state
 * (`synced`/`discarded`) whose `updatedAt` is older than
 * `OUTBOX_RETENTION_MS`, along with any of its still-linked blobs. A row
 * with a missing or unparsable `updatedAt` is left alone (fail safe —
 * never guess an age to justify a delete). `now` is injectable for
 * deterministic tests, same pattern as `foreground-drainer.ts`'s own `now`
 * dep. Called at boot (`main.tsx`, fire-and-forget, alongside the existing
 * blob sweeps) AND after every successful sync (`foreground-drainer.ts`,
 * optional `purgeOldOutbox` dep) — never on the `/pending` render path
 * itself, so opening that page is never what pays for the sweep.
 */
export async function purgeOldOutboxRows(db: OfflineDb, now: Date = new Date()): Promise<number> {
  const cutoff = now.getTime() - OUTBOX_RETENTION_MS;
  const rows = await db.outbox.toArray();
  let purged = 0;

  for (const row of rows) {
    if (!isPurgeableState(row.state)) continue;
    const updatedAtRaw = row.updatedAt;
    if (typeof updatedAtRaw !== 'string') continue;
    const updatedAtMs = new Date(updatedAtRaw).getTime();
    if (Number.isNaN(updatedAtMs) || updatedAtMs > cutoff) continue;

    await db.blobs.where('outboxId').equals(row.id).delete();
    await db.outbox.delete(row.id);
    purged += 1;
  }

  return purged;
}
